// Canlı Durum — "Bu hafta" şeridi (görev #195 Vegas / #196 Prime, 2026-10-09): Pazartesi 00:00
// İstanbul'dan şu ana kadar marka toplamları (güncel yatırım, çağrı, SMS, WhatsApp-Telegram, QMS
// ortalaması, kritik hata, başarı ortalaması).
//
// Veri İKİNCİ bir yol DEĞİL: `GET /api/weekly-report?week=current` — Haftalık Rapor'un aynı yanıtı,
// aynı L1/L2 cache, aynı soğuk-hesap kapısı. Sunucu cari haftayı 5 dk'da bir yeniden tarar
// (TTL_LIVE_MS, uçuştaki istek paylaşımlı), o yüzden şerit de 5 dk'da bir sorar: daha sık sormak
// yeni sayı getirmez, yalnız her yanıtta QMS'e giden çağrıyı çoğaltır. Sekme gizliyken DURUR.
// Sayıların tanımı web/src/week-kpis.ts'te.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, getWeeklyReport } from '../api';
import { brandLabel, LIVE_WEEK_BRANDS } from '../constants';
import { fmtMoney, fmtPct1 } from '../format';
import type { BrandId, WeeklyReportData } from '../types';
import { isWeeklyBrandError } from '../types';
import { cardStyle, Icon, Skeleton } from '../ui';
import { isRoutinePendingCaveat, weekKpis } from '../week-kpis';
import { scoreColor, weeklyRealCalls } from './WeeklyReport';

const REFRESH_MS = 5 * 60_000;
/** 429 `busy` yanıtında `Retry-After` okunamazsa. */
const BUSY_FALLBACK_SEC = 30;

// Başlıklar sütunu uçtan uca dolduruyordu (EN etiketleri ~108px, sütun ~110px, ara 10px): komşu başlıklar
// tek etiket gibi okunuyordu ("WHATSAPP - TELEGRAM QMS AVERAGE"). Geniş ara + başlık sarar, satır altta hizalı.
const COLS = 'minmax(72px,.8fr) repeat(7,minmax(92px,1fr))';
const GAP = '20px';
const MIN_WIDTH = '920px';

const HEAD: React.CSSProperties = {
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '.04em',
  color: 'var(--faint)',
  lineHeight: 1.25,
  textWrap: 'balance',
};

const VALUE: React.CSSProperties = {
  fontSize: '17px',
  fontWeight: 700,
  color: 'var(--text)',
  lineHeight: 1.1,
  fontVariantNumeric: 'tabular-nums',
};

/** /10, TR virgülü; null → BOŞ (§6 — karnedeki `—` burada yok). */
const fmtScore1 = (v: number | null) => (v == null ? '' : v.toFixed(1).replace('.', ','));

// Hücre başına TEK sayı (görev #195/#196'nın istediği). Alt satırlar (48s, cevaplama, başarısız SMS, değerlendirme)
// sahip 2026-11-03 "mushed up" dedi → kaldırıldı; ayrıntı Haftalık Rapor'da.

export default function LiveWeekStrip({ brands, hidden }: { brands: BrandId[]; hidden: boolean }) {
  const scope = LIVE_WEEK_BRANDS.filter((b) => brands.includes(b));
  const scopeKey = scope.join(',');
  const [data, setData] = useState<WeeklyReportData | null>(null);
  const [failed, setFailed] = useState(false);
  const [busyUntil, setBusyUntil] = useState<number | null>(null);
  const seq = useRef(0);
  const autoRetried = useRef(false);

  const load = useCallback(async () => {
    if (!scopeKey) return;
    const my = ++seq.current;
    try {
      const d = await getWeeklyReport('current', scopeKey.split(',') as BrandId[]);
      if (my !== seq.current) return;
      setData(d);
      setFailed(false);
      setBusyUntil(null);
      autoRetried.current = false;
    } catch (e) {
      if (my !== seq.current) return;
      // Geçici hata eldeki sayıları SİLMEZ; bir sonraki tur düzeltir.
      if (e instanceof ApiError && e.status === 429 && !autoRetried.current) {
        // Soğuk-hesap kapısı dolu: TEK otomatik deneme (sonsuz döngü kapıyı beslerdi), sonrası 5 dk'lık tur.
        autoRetried.current = true;
        const sec = e.retryAfter && e.retryAfter > 0 ? Math.min(e.retryAfter, 300) : BUSY_FALLBACK_SEC;
        setBusyUntil(Date.now() + sec * 1000);
      } else {
        setFailed(true);
        setBusyUntil(null);   // a second 429 ends the auto-retry: no extra load on every return to the tab
      }
    }
  }, [scopeKey]);

  // A new brand scope starts from a clean slate: the previous scope's numbers (or failure) are not this one's, and
  // keeping them showed the column headers with ZERO rows and no message until the next good tick.
  useEffect(() => {
    setData(null);
    setFailed(false);
    setBusyUntil(null);
    autoRetried.current = false;
  }, [scopeKey]);

  useEffect(() => {
    if (hidden || !scopeKey) return;
    void load();
    const id = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(id);
  }, [hidden, scopeKey, load]);

  useEffect(() => {
    if (busyUntil === null || hidden) return;
    const t = setTimeout(() => void load(), Math.max(0, busyUntil - Date.now()));
    return () => clearTimeout(t);
  }, [busyUntil, hidden, load]);

  if (!scopeKey) return null;

  const rows = data ? scope.filter((b) => data.brands[b]) : [];

  return (
    <div style={{ ...cardStyle, borderRadius: '12px', marginBottom: '20px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <Icon name="date_range" size={17} color="var(--dim)" style={{ flex: 'none' }} />
        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Bu hafta</span>
        <span style={{ fontSize: '11.5px', color: 'var(--faint)' }}>Pazartesi 00:00'dan beri</span>
      </div>

      {!data && !failed && busyUntil === null && (
        <div style={{ padding: '12px 16px' }}>
          <Skeleton heights={[46]} />
        </div>
      )}
      {!data && (failed || busyUntil !== null) && (
        <div style={{ padding: '16px', fontSize: '12.5px', color: 'var(--faint)' }}>
          {failed ? 'Bu haftanın toplamları okunamadı.' : 'Bu hafta hesaplanıyor…'}
        </div>
      )}

      {data && (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: MIN_WIDTH }}>
            <div style={{ display: 'grid', gridTemplateColumns: COLS, columnGap: GAP, alignItems: 'end', padding: '9px 16px', borderBottom: '1px solid var(--border)' }}>
              <span style={HEAD}>MARKA</span>
              <span style={HEAD} title="Pencereleri henüz kapanmamış görüşmeler eklendikçe artar">GÜNCEL YATIRIM (24S)</span>
              <span style={HEAD}>TOPLAM ÇAĞRI</span>
              <span style={HEAD}>TOPLAM SMS</span>
              <span style={HEAD}>{'WHATSAPP - TELEGRAM'}</span>
              <span style={HEAD}>QMS ORTALAMASI</span>
              <span style={HEAD}>TOPLAM KRİTİK HATA</span>
              <span style={HEAD}>BAŞARI ORT. (24S)</span>
            </div>
            {rows.map((b, i) => {
              const slot = data.brands[b];
              const last = i === rows.length - 1;
              const rowStyle: React.CSSProperties = {
                display: 'grid',
                gridTemplateColumns: COLS,
                columnGap: GAP,
                padding: '11px 16px',
                alignItems: 'start',
                borderBottom: last ? 'none' : '1px solid var(--border)',
              };
              const brandCell = (
                <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', paddingTop: '2px', whiteSpace: 'nowrap' }}>
                  {brandLabel(b)}
                </span>
              );
              if (!slot || isWeeklyBrandError(slot)) {
                return (
                  <div key={b} style={rowStyle}>
                    {brandCell}
                    <span style={{ gridColumn: '2 / -1', fontSize: '12.5px', color: 'var(--faint)', paddingTop: '2px' }}>Bu hafta okunamadı</span>
                  </div>
                );
              }
              const k = weekKpis(slot, weeklyRealCalls(slot));
              const caveats = slot.caveats || [];
              return (
                <div key={b} style={rowStyle}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
                    {brandCell}
                    {/* Son başarılı taramadan sunuluyorsa ya da sekme eksikse rakama GÜVENME sebebi. */}
                    {caveats.some((c) => !isRoutinePendingCaveat(c)) && (
                      <span title={caveats.join('\n')} style={{ display: 'flex', flex: 'none' }}>
                        <Icon name="report" size={15} color="#e0a23c" />
                      </span>
                    )}
                  </span>
                  <div style={VALUE}>{fmtMoney(k.deposit24h)}</div>
                  <div style={VALUE}>{fmtMoney(k.calls)}</div>
                  <div style={VALUE}>{fmtMoney(k.smsSent)}</div>
                  <div style={VALUE}>{fmtMoney(k.messages)}</div>
                  <div style={{ ...VALUE, color: scoreColor(k.qmsAvg) ?? 'var(--text)' }}>{fmtScore1(k.qmsAvg)}</div>
                  <div style={{ ...VALUE, color: k.criticals ? 'var(--bad)' : 'var(--text)' }}>{fmtMoney(k.criticals)}</div>
                  <div style={VALUE}>{fmtPct1(k.successRate24hPct)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
